import React, { useContext } from 'react';

import UserAvatarV2 from 'common/common/UserAvatarV2';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { IsWidgetContext } from 'common/containers/IsWidgetContainer';
import { TintColorContext } from 'common/containers/TintColorContainer';
import Link from 'common/Link';
import { cn } from 'common/style/utils';
import truncateString from 'common/truncateString';
import HStack from 'common/ui/HStack';

import type { Company } from 'common/api/endpoints/companies';
import type { HexColor } from 'common/colors/utils';

interface ThirdPartyCompany {
  _id: string;
  name: string;
}

interface UserWithCompanies {
  _id: string | null;
  aliasID?: string;
  alias?: string;
  avatarURL: string | null;
  name: string;
  urlName?: string | null;
  companies?: ThirdPartyCompany[];
  serviceAccount?: boolean;
  created?: string;
}

interface Props {
  containerClassName?: string;
  className?: string;
  showAlias?: boolean;
  showAvatar?: boolean;
  showBadge?: boolean;
  showCompanyNames?: boolean;
  showProfile?: boolean;
  showTint?: boolean;
  targetURL?: string | null;
  truncate?: boolean;
  user: UserWithCompanies;
}

const UserLockupV2 = ({
  containerClassName,
  className,
  showAlias = false,
  showAvatar = true,
  showBadge = true,
  showCompanyNames = false,
  showProfile = true,
  showTint = true,
  targetURL = null,
  truncate = false,
  user,
}: Props) => {
  const company = useContext<Company>(CompanyContext);
  const isWidget = useContext<boolean>(IsWidgetContext);
  const tintColor = useContext<HexColor | null>(TintColorContext);

  const renderCompanyNames = () => {
    if (!showCompanyNames || !user.companies || !user.companies.length) {
      return null;
    }

    if (user.companies.length === 1) {
      return <span className="text-xs text-slate-500">{'\u00a0at ' + user.companies[0].name}</span>;
    }

    return (
      <span className="text-base text-slate-500">
        {'\u00a0at ' + user.companies[0].name + ' and ' + (user.companies.length - 1) + ' more'}
      </span>
    );
  };

  const renderName = () => {
    if (!company || !company.members) {
      return null;
    }

    const isUserMember = company.members.some((member) => member._id === user._id);
    const truncatedName = truncateString(user.name, 20);
    const truncatedNameWithAlias = user.alias
      ? `${truncateString(user.name, 20)} (${user.alias})`
      : truncatedName;
    const nameWithAlias = user.alias ? `${user.name} (${user.alias})` : user.name;

    const names = {
      truncated: {
        showAlias: truncatedNameWithAlias,
        hideAlias: truncatedName,
      },
      full: {
        showAlias: nameWithAlias,
        hideAlias: user.name,
      },
    };
    const nameGroup = truncate ? names.truncated : names.full;
    const name = !isUserMember && showAlias ? nameGroup.showAlias : nameGroup.hideAlias;

    if (!isUserMember) {
      return <span className="text-sm font-medium text-slate-900">{name}</span>;
    }

    const nameStyle = showTint && tintColor ? { color: tintColor } : undefined;
    const nameClassName = cn('text-base font-medium', {
      'font-semibold': showTint,
    });

    return (
      <span className={nameClassName} style={nameStyle}>
        {name}
      </span>
    );
  };

  const renderContents = () => {
    const content = (
      <>
        {showAvatar && <UserAvatarV2 user={user} size={28} showBadge={showBadge} />}
        <span className="flex flex-col group-hover:bg-black/10 rounded-sm px-1 transition-hover">
          {renderName()}
          {renderCompanyNames()}
        </span>
      </>
    );

    const isLinkable =
      company?.viewerIsMember && showProfile && !isWidget && user.urlName && !user.serviceAccount;

    if (!isLinkable) {
      return <HStack className={cn('items-center gap-2', className)}>{content}</HStack>;
    }

    const userProfileURL =
      targetURL ?? `/admin/users/${user.urlName}?search=${encodeURIComponent(user.name)}`;

    return (
      <Link
        className={cn('group flex items-center gap-2 hover:opacity-90', className)}
        to={userProfileURL}>
        {content}
      </Link>
    );
  };

  return <div className={cn('inline-flex', containerClassName)}>{renderContents()}</div>;
};

export default UserLockupV2;
